import { memo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import {
  FaSolarPanel,
  FaTemperatureHigh,
  FaChartLine,
  FaLeaf,
} from "react-icons/fa";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.8 },
  },
};

const fadeInUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const iconMap = {
  FaSolarPanel: FaSolarPanel,
  FaTemperatureHigh: FaTemperatureHigh,
  FaChartLine: FaChartLine,
  FaLeaf: FaLeaf,
};

const iconColorMap = {
  FaSolarPanel: "text-yellow-400",
  FaTemperatureHigh: "text-red-400",
  FaChartLine: "text-green-400",
  FaLeaf: "text-green-400",
};

const FeaturedPVTModels = ({ pvtImage }) => {
  const { t } = useTranslation();

  const featuredPVT = t("components.home.featuredPVTModels", {
    returnObjects: true,
  });

  const featuredPanel = featuredPVT.featuredPanel;

  const benefits = featuredPanel.benefits;

  return (
    <section className="bg-black text-gray-100 py-16 px-6" id="safePad">
      <motion.div
        className="max-w-7xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
          <motion.div
            className="relative"
            variants={fadeInUpVariant}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <div className="relative overflow-hidden rounded-xl shadow-2xl border border-gray-800">
              <img
                src={pvtImage}
                alt={featuredPanel.title}
                className="w-full rounded-xl"
              />
              <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-gray-900 to-transparent"></div>
            </div>
          </motion.div>

          <div className="flex flex-col space-y-6">
            <motion.div className="space-y-4" variants={fadeInUpVariant}>
              <h3 className="text-xl md:text-2xl font-bold text-white">
                {featuredPanel.title}
                <span className="block mt-1 text-lg font-normal text-gray-400">
                  {featuredPanel.subtitle}
                </span>
              </h3>
              <p className="text-gray-300">{featuredPanel.description}</p>
            </motion.div>

            <motion.div className="space-y-4" variants={staggerChildren}>
              <h4 className="text-md font-semibold text-gray-200">
                {featuredPanel.keyBenefits || "Key Benefits:"}
              </h4>
              <motion.ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {benefits.map((benefit, index) => {
                  const IconComponent = iconMap[benefit.icon] || FaSolarPanel;
                  const colorClass =
                    iconColorMap[benefit.icon] || "text-yellow-400";

                  return (
                    <motion.li
                      key={index}
                      className="flex items-center gap-3 bg-gray-800/50 p-3 rounded-lg"
                      variants={itemVariant}
                      whileHover={{ y: -5 }}
                    >
                      <IconComponent
                        className={`${colorClass} text-xl flex-shrink-0`}
                      />
                      <span className="text-sm">{benefit.text}</span>
                    </motion.li>
                  );
                })}
              </motion.ul>
            </motion.div>

            <motion.div className="pt-4" variants={fadeInUpVariant}>
              <Link to="/pvt" className="inline-block w-auto">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white px-6 py-3 rounded-lg shadow-lg inline-flex items-center justify-center w-full cursor-pointer"
                >
                  {featuredPanel.cta || "Explore Our PVT Technology"}
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

FeaturedPVTModels.propTypes = {
  pvtImage: PropTypes.string.isRequired,
};

export default memo(FeaturedPVTModels);
